import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/context/ThemeContext";
import { NewsItem } from "@/types/news";
import { formatDateShort } from "@/utils/dateUtils";
import { getNewsUITextLabel } from "@/utils/newsUtils";
import { getLanguage } from "@/utils/languageUtils";
import { formatRichTextToCardPreview } from "@/utils/formatRichText";
import { useNewsFullSizeImage } from "@/hooks/useNewsFullSizeImage";

interface NewsCardProps {
  news: NewsItem;
  index: number;
  onNewsClick: (news: NewsItem) => void;
}

const NewsCard = ({ news, index, onNewsClick }: NewsCardProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const imageUrl = useNewsFullSizeImage(news.image?.id, news.image?.url);

  const truncateText = (text: string | undefined, maxLength: number) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trimEnd() + "...";
  };

  return (
    <Card
      className={`relative overflow-hidden transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] animate-fade-in cursor-pointer h-full min-h-[250px]`}
      style={{ animationDelay: `${index * 100}ms` }}
      onClick={() => onNewsClick(news)}
    >
      {/* Background image with tint */}
      {news.image && imageUrl && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${imageUrl})` }}
        />
      )}

      {/* Dark tint overlay */}
      <div
        className={`absolute inset-0 ${
          news.image ? "bg-black/70" : isDark ? "bg-black/20" : "bg-white/90"
        }`}
      />

      {/* Border */}
      <div
        className={`absolute inset-0 border ${
          isDark ? "border-white/20" : "border-black/10"
        }`}
      />

      <CardContent className="relative z-10 p-6 h-full flex flex-col">
        <div className="flex flex-col gap-4 h-full">
          {/* Header with badges and date */}
          <div className="flex justify-between items-start gap-2">
            <div className="flex flex-wrap gap-1">
              {news.labels &&
                news.labels.slice(0, 2).map((label, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="text-xs bg-white/90 text-gray-800 border-gray-300 dark:bg-black/80 dark:text-white dark:border-gray-600"
                  >
                    {label}
                  </Badge>
                ))}
            </div>
            <span
              className={`text-sm ${
                news.image
                  ? "text-white/90"
                  : isDark
                  ? "text-white/70"
                  : "text-gray-600"
              } whitespace-nowrap`}
            >
              {formatDateShort(news.createdAt, getLanguage() === "en" ? "en-US" : "sv-SE")}
            </span>
          </div>

          {/* Title */}
          <h3
            className={`font-semibold text-xl ${
              news.image
                ? "text-white"
                : isDark
                ? "text-white"
                : "text-gray-900"
            } leading-tight`}
          >
            {truncateText(news.title, 60)}
          </h3>

          {/* Single-block preview HTML so line-clamp works with breaks + inline formatting */}
          <p
            className={`${
              news.image
                ? "text-white/90"
                : isDark
                ? "text-white/80"
                : "text-gray-700"
            } text-sm line-clamp-3 flex-grow leading-snug break-words [&_strong]:font-semibold`}
            dangerouslySetInnerHTML={{
              __html: formatRichTextToCardPreview(news.content || ""),
            }}
          />

          {/* Read more indicator */}
          <div
            className={`text-xs ${
              news.image
                ? "text-white/80"
                : isDark
                ? "text-white/60"
                : "text-gray-500"
            } mt-auto`}
          >
            {getNewsUITextLabel("clickToReadMore")}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NewsCard;
